import { ref, reactive, computed, watch } from 'vue'
import type { Session, SessionRequest } from '~/types/administration'
import type { ApiResponse } from '~/types/api'
import { useApi } from './useApi'
import { useClasses } from './useClasses'
import { toast } from 'vue3-toastify'
import { sortSessionsByEtat } from '@/utils/sessionHelpers'
import { applyHistoriqueParams, shouldSkipActiveFilter } from '@/utils/historiqueParams'

interface SessionState {
    sessions: Session[]
    session: Session | null
    loading: boolean
    errors: Record<string, string[]>
    filters: {
        search: string
        status: 'Tous' | 'Actifs' | 'Inactifs'
        classe_id: number | null
        page: number
        per_page: number
    }
    pagination: {
        total: number
        current_page: number
        per_page: number
    }
}

export function useSession() {
    const { apiData } = useApi()
    const { classes: allClasses, fetchClasses: fetchAllClasses } = useClasses()

    const state = reactive<SessionState>({
        sessions: [],
        session: null,
        loading: false,
        errors: {},
        filters: {
            search: '',
            status: 'Tous',
            classe_id: null,
            page: 1,
            per_page: 50
        },
        pagination: {
            total: 0,
            current_page: 1,
            per_page: 50
        }
    })

    const formRef = ref()
    const loadingState = ref(false)
    /** Mode historique local à l'instance (survît aux refetch des watchers filtres). */
    const includeHistorique = ref(false)
    const anneeUniversitaire = ref<string | null>(null)
    let searchTimeout: NodeJS.Timeout | null = null
    // Normalise une chaîne/date en 'YYYY-MM-DD 00:00:00'
    function normalizeDateToMidnight(input: string | Date | null | undefined): string | null {
        if (!input) return null;
        let date: Date
        if (input instanceof Date) {
            date = input
        } else if (typeof input === 'string') {
            // Accepte 'YYYY-MM-DD' ou 'YYYY-MM-DD HH:mm:ss' ou ISO
            const str = input.trim()
            if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
                // Ajoute T00:00:00 pour parsing fiable
                date = new Date(str + 'T00:00:00')
            } else if (/^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}(:\d{2})?$/.test(str)) {
                // Remplace l'espace par T
                date = new Date(str.replace(' ', 'T'))
            } else {
                date = new Date(str)
            }
        } else {
            return null
        }
        if (isNaN(date.getTime())) return null
        const y = date.getFullYear()
        const m = String(date.getMonth() + 1).padStart(2, '0')
        const d = String(date.getDate()).padStart(2, '0')
        return `${y}-${m}-${d} 00:00:00`
    }

    const headers = [
        { title: 'Code', align: 'start' as const, key: 'code', sortable: true },
        { title: 'Nom', align: 'start' as const, key: 'nom', sortable: true },
        { title: 'Classe', align: 'start' as const, key: 'classe.code', sortable: true },
        { title: 'Format', align: 'start' as const, key: 'format', sortable: true },
        { title: 'Année universitaire', align: 'start' as const, key: 'annee_universitaire', sortable: true },
        { title: 'État', align: 'start' as const, key: 'etat', sortable: true },
        { title: 'Actif', align: 'start' as const, key: 'is_active', sortable: true },
        { title: 'Actions', align: 'center' as const, key: 'actions', sortable: false }
    ]

    const formats = ['Cours du jour', 'Cours du soir']
    const modes = ['présentiel', 'hybride', 'en ligne']
    const etats = ['en cours', 'annulé', 'prévu']
    /** États API complets (affichage / legacy) */
    const etatsTous = ['en cours', 'annulé', 'terminé', 'prévu']
    const statuts = ['ouvert aux inscriptions', 'fermé aux inscriptions']

    const rules = {
        nom: [(v: string) => !!v || 'Le nom est requis', (v: string) => v.length <= 255 || 'Le nom ne doit pas dépasser 255 caractères'],
        format: [(v: string) => !!v || 'Le format est requis', (v: string) => formats.includes(v) || 'Format invalide'],
        mode: [(v: string) => !!v || 'Le mode est requis', (v: string) => modes.includes(v) || 'Mode invalide'],
        etat: [
            (v: string) => !!v || 'L\'état est requis',
            (v: string) => {
                if (etatsTous.includes(v)) return true
                return 'État invalide'
            },
            // 'terminé' est un état persisté valide (clôture) : autorisé en édition archive.
            // La transition vers terminé est bloquée dans le formulaire (handlers create / select).
        ],
        statut: [(v: string) => !!v || 'Le statut est requis', (v: string) => statuts.includes(v) || 'Statut invalide'],
        date_rentree_previsionnel: [
            (v: string) => !!v || 'La date de rentrée prévisionnelle est requise'
        ],
        date_fin_previsionnel: [
            (v: string) => !!v || 'La date de fin prévisionnelle est requise'
        ],
        classe_id: [(v: number) => !!v || 'La classe est requise'],
        responsable_pedagogique_principal_id: [(v: any) => !!v || 'Le responsable pédagogique principal est requis']
    }

    const filteredSessions = computed(() => {
        const filtered = state.sessions.filter(session => {
            const searchTerm = state.filters.search.toLowerCase().trim();
            
            // Log de débogage pour vérifier la normalisation
            if (searchTerm) {
            }
            
            const matchSearch = !searchTerm || 
                session.code?.toLowerCase().includes(searchTerm) ||
                session.nom?.toLowerCase().includes(searchTerm);
            
            const matchStatus = state.filters.status === 'Tous' ||
                shouldSkipActiveFilter({
                    includeHistorique: includeHistorique.value,
                    anneeUniversitaire: anneeUniversitaire.value,
                }) ||
                (state.filters.status === 'Actifs' && session.is_active) ||
                (state.filters.status === 'Inactifs' && !session.is_active);
            
            const matchClasse = !state.filters.classe_id || 
                session.classe_id === state.filters.classe_id;
            
            return matchSearch && matchStatus && matchClasse;
        });
        
        // Trier les sessions selon l'état : en cours → prévu → terminé → annulé
        return sortSessionsByEtat(filtered);
    });

    async function fetchSessions(options?: {
        per_page?: number
        classe_id?: number
        selectedGroup?: string | null
        withRelations?: boolean
        includeHistorique?: boolean
        anneeUniversitaire?: string | null
    }) {
        try {
            loadingState.value = true;
            if (options?.includeHistorique !== undefined) {
                includeHistorique.value = options.includeHistorique
            }
            if (options?.anneeUniversitaire !== undefined) {
                anneeUniversitaire.value = options.anneeUniversitaire
            }
            const params = new URLSearchParams();
            // Ne pas envoyer 'search' au backend; filtrage en front pour l'insensibilité à la casse
            // En historique, ne pas forcer is_active sinon les sessions clôturées restent invisibles
            if (
                state.filters.status !== 'Tous' &&
                !shouldSkipActiveFilter({
                    includeHistorique: includeHistorique.value,
                    anneeUniversitaire: anneeUniversitaire.value,
                })
            ) {
                params.append('is_active', (state.filters.status === 'Actifs').toString());
            }
            // Utiliser le filtre temporaire ou celui du state
            const classeId = options?.classe_id || state.filters.classe_id;
            if (classeId) {
                params.append('classe_id', classeId.toString());
            }
            // Charger tout pour pagination client uniforme
            params.append('per_page', 'all');
            // Inclure les relations classe et classe_session seulement si explicitement demandé (pour rétrocompatibilité)
            if (options?.withRelations === true) {
                params.append('with', 'classe,classe_session');
            }
            applyHistoriqueParams(params, {
                includeHistorique: includeHistorique.value,
                anneeUniversitaire: anneeUniversitaire.value,
            })

            const response = await apiData<ApiResponse<Session[]>>(`/sessions?${params.toString()}`, {
                "X-Selected-Group": options?.selectedGroup
            } as any);

            if (response?.success) {
                state.sessions = response.data;
                state.pagination = {
                    total: response.data.length,
                    current_page: 1,
                    per_page: state.filters.per_page
                };
            }
        } catch (error) {
            console.error('Error in fetchSessions:', error);
            state.sessions = [];
            // Propager l'erreur pour que l'appelant puisse la gérer
            throw error;
        } finally {
            loadingState.value = false;
        }
    }

    // Watchers ciblés avec debounce pour éviter les requêtes multiples
    let filtersTimeout: NodeJS.Timeout | null = null
    
    watch(() => state.filters.status, () => {
        if (filtersTimeout) clearTimeout(filtersTimeout)
        filtersTimeout = setTimeout(() => fetchSessions(), 150)
    })
    watch(() => state.filters.classe_id, () => {
        if (filtersTimeout) clearTimeout(filtersTimeout)
        filtersTimeout = setTimeout(() => fetchSessions(), 150)
    })
    watch(() => state.filters.search, () => {
        if (searchTimeout) clearTimeout(searchTimeout)
        searchTimeout = setTimeout(() => fetchSessions(), 300)
    })

    async function fetchSession(id: number, withRelations: boolean = false) {
        try {
            state.loading = true
            const url = withRelations 
                ? `/sessions/${id}?with=classe,classe_session`
                : `/sessions/${id}`
            const response = await apiData<ApiResponse<Session>>(url)
            
            if (response.success) {
                state.session = response.data
                return response.data
            }
            return null
        } catch (error) {
            //console.error('Error in fetchSession:', error)
            return null
        } finally {
            state.loading = false
        }
    }

    async function createSession(data: SessionRequest) {
        errorsClear();
        try {
            const dateRentree = normalizeDateToMidnight(data.date_rentree_previsionnel) || ''
            const dateFin = normalizeDateToMidnight(data.date_fin_previsionnel) || ''
            const annee_universitaire = computeAnneeUniversitaire(dateRentree);
            const payload = { 
                ...data, 
                date_rentree_previsionnel: dateRentree,
                date_fin_previsionnel: dateFin,
                annee_universitaire 
            };
            const response = await apiData<ApiResponse<Session>>('/sessions', {
                method: 'POST',
                body: payload
            })
            
            if (response.success) {
                await fetchSessions()
                return { success: true, message: response.message || 'Session créée avec succès' }
            }

            if (response.errors) {
                state.errors = response.errors
            }
            return { success: false, message: response.message || "Une erreur s'est produite" }
        } catch (error: any) {
            const response = error?.response?._data
            if (response?.errors) {
                state.errors = response.errors
            }
            return { success: false, message: response?.message || "Une erreur s'est produite" }
        }
    }

    async function updateSession(id: number, data: SessionRequest) {
        errorsClear();
        try {
            const dateRentree = normalizeDateToMidnight(data.date_rentree_previsionnel) || ''
            const dateFin = normalizeDateToMidnight(data.date_fin_previsionnel) || ''
            const annee_universitaire = computeAnneeUniversitaire(dateRentree);
            const payload = { 
                ...data, 
                date_rentree_previsionnel: dateRentree,
                date_fin_previsionnel: dateFin,
                annee_universitaire 
            };
            const response = await apiData<ApiResponse<Session>>(`/sessions/${id}`, {
                method: 'PUT',
                body: payload
            })
            
            if (response.success) {
                await fetchSessions()
                return { success: true, message: response.message || 'Session modifiée avec succès' }
            }

            if (response.errors) {
                state.errors = response.errors
            }
            return { success: false, message: response.message || "Une erreur s'est produite" }
        } catch (error: any) {
            const response = error?.response?._data
            if (response?.errors) {
                state.errors = response.errors
            }
            return { success: false, message: response?.message || "Une erreur s'est produite" }
        }
    }

    async function deleteSession(id: number) {
        try {
            const response = await apiData<ApiResponse<null>>(`/sessions/${id}`, {
                method: 'DELETE'
            })
            if (response.success) {
                await fetchSessions()
                return { success: true, message: response.message || 'Session supprimée avec succès' }
            }
            return { 
                success: false, 
                message: response.message || "Une erreur s'est produite lors de la suppression" 
            }
        } catch (error: any) {
            return {
                success: false,
                message: error?.response?._data?.message || error?.data?.message || "Une erreur s'est produite lors de la suppression"
            }
        }
    }

    async function toggleActive(id: number) {
        try {
            const response = await apiData<ApiResponse<Session>>(`/sessions/${id}/toggle-active`, {
                method: 'PATCH'
            })
            
            if (response.success) {
                await fetchSessions()
                return { success: true, message: response.message || 'Statut de la session modifié avec succès' }
            }
            return { success: false, message: response.message || "Erreur lors de la modification du statut" }
        } catch (error) {
            //console.error('Error in toggleActive:', error)
            return { success: false, message: "Erreur lors de la modification du statut" }
        }
    }

    function resetForm(initialData?: Partial<SessionRequest>) {
        if (formRef.value) {
            formRef.value.resetValidation()
        }
        // Réinitialiser complètement les erreurs
        Object.keys(state.errors).forEach(key => {
            delete state.errors[key]
        })
        state.errors = {}
        
        const defaults = { 
            nom: '',
            date_debut: '',
            date_fin: '',
            classe_id: null as unknown as number,
            responsable_pedagogique_principal_id: null as number | null,
            responsable_pedagogique_suppleant_id: null as number | null,
            is_active: true
        };
        return { ...defaults, ...initialData };
    }

    function errorsClear() {
        // Réinitialiser complètement les erreurs
        Object.keys(state.errors).forEach(key => {
            delete state.errors[key]
        })
        state.errors = {}
    }

    // Nouvelle fonction pour réinitialiser complètement le store
    function resetStore() {
        state.sessions = []
        state.session = null
        state.loading = false
        state.errors = {}
        state.filters = {
            search: '',
            status: 'Tous',
            classe_id: null,
            page: 1,
            per_page: 50
        }
        state.pagination = {
            total: 0,
            current_page: 1,
            per_page: 50
        }
    }

    function fetchClassesForSelect() {
        fetchAllClasses();
    }

    function computeAnneeUniversitaire(dateRentree: string): string {
        if (!dateRentree) return '';
        const date = new Date(dateRentree.replace(' ', 'T'));
        const yearStart = date.getFullYear();
        const yearEnd = yearStart + 1;
        return `${yearStart}/${yearEnd}`;
    }

    return {
        sessions: filteredSessions,
        allSessions: computed(() => state.sessions),
        session: state.session,
        loading: loadingState,
        errors: state.errors,
        filters: state.filters,
        pagination: state.pagination,
        formRef,
        headers,
        rules,
        formats,
        modes,
        etats,
        etatsTous,
        statuts,
        allClasses,
        includeHistorique,
        anneeUniversitaire,
        fetchSessions,
        fetchSession,
        createSession,
        updateSession,
        deleteSession,
        toggleActive,
        resetForm,
        errorsClear,
        fetchClassesForSelect,
        computeAnneeUniversitaire,
        resetStore
    }
} 